import { api } from '../api.ts';
import { compact, html, mount, raw } from '../html.ts';
import { formatDate, formatMoney, HEALTH, isOverdue, options, PROJECT_STATUS, t, tk } from '../i18n.ts';
import { navigate } from '../router.ts';
import { canWrite, invalidate, lookups, state } from '../state.ts';
import { badge, emptyState, field, openFormDialog, optionList, riskTone, select, statusBadge, textarea, toast } from '../ui.ts';
import { pageHeader } from './common.ts';

export function healthBadge(project: { health: string | null; suggested_health?: string }) {
  if (project.health) return statusBadge(project.health, HEALTH);
  // Derived health would be computed from the guest's partial view (e.g. without
  // hidden risks); guests only see a health the team set explicitly.
  if (project.suggested_health && state.me?.role !== 'guest') {
    return html`<span class="pma-suggested" title="${t('Automatisch abgeleitet aus Terminen, Risiken und Gates')}">${statusBadge(project.suggested_health, HEALTH)}</span>`;
  }
  return badge('–');
}

async function projectFields(project: any = {}) {
  const [companies, members] = await Promise.all([lookups.companies(), lookups.members()]);
  return html`
    ${field(t('Name'), 'name', 'text', { required: true, value: project.name, span: true })}
    ${field(t('Kürzel'), 'key', 'text', {
      required: true,
      value: project.key,
      hint: t('2–10 Großbuchstaben/Ziffern, z. B. PMA'),
      attrs: 'pattern="[A-Z][A-Z0-9]{1,9}" maxlength="10" data-uppercase',
    })}
    ${select(t('Kunde'), 'client_id', optionList(companies, project.client_id, t('Kein Kunde')))}
    ${select(t('Status'), 'status', options(PROJECT_STATUS, project.status ?? 'active'))}
    ${select(t('Ampel'), 'health', options(HEALTH, project.health, t('Automatisch')))}
    ${field(t('Start'), 'starts_on', 'date', { value: project.starts_on })}
    ${field(t('Ende'), 'ends_on', 'date', { value: project.ends_on })}
    ${field(t('Budget'), 'budget_planned', 'number', { value: project.budget_planned, attrs: 'min="0" step="100"' })}
    ${select(t('Verantwortlich'), 'owner_user_id', optionList(members, project.owner_user_id, t('Niemand')))}
    ${textarea(t('Beschreibung'), 'description', { value: project.description, rows: 3 })}
  `;
}

function wireUppercase(dialog: HTMLElement) {
  for (const input of dialog.querySelectorAll<HTMLInputElement>('[data-uppercase]')) {
    input.addEventListener('input', () => {
      input.value = input.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    });
  }
}

export async function openProjectCreate(): Promise<void> {
  const [{ items: templates }, fields] = await Promise.all([api.get('/templates'), projectFields()]);
  openFormDialog({
    title: t('Neues Projekt'),
    description: t('Eine Vorlage legt Phasen, Gates mit Kriterien, Startaufgaben und Kennzahlen an.'),
    wide: true,
    fields: html`${fields}
      <fieldset class="p-fieldset pma-span-2 pma-templates">
        <legend class="p-label">${t('Vorlage')}</legend>
        ${templates.map(
          (tpl: any, i: number) => html`<label class="pma-template">
            <input type="radio" name="template_id" value="${tpl.id}"${i === 0 ? raw(' checked') : ''}>
            <span><strong>${tpl.name}</strong><span class="pma-muted pma-small">${tpl.description}</span>
            ${tpl.phases.length ? html`<span class="pma-template__phases">${tpl.phases.map((p: any) => html`<span>${p.name} · ${t('{n} W', { n: p.weeks })}${p.gates.length ? html` <b>${p.gates.join(' ')}</b>` : ''}</span>`)}</span>` : ''}</span>
          </label>`,
        )}
      </fieldset>`,
    submitLabel: t('Projekt anlegen'),
    onOpen: wireUppercase,
    onSubmit: async (data) => {
      const created = await api.post('/projects', compact(data));
      toast(t('Projekt angelegt'), `${created.key} · ${created.name}`);
      navigate(`/projects/${created.id}`);
    },
  });
}

export async function openProjectEdit(project: any, onSaved: () => void): Promise<void> {
  openFormDialog({
    title: t('{key} bearbeiten', { key: project.key }),
    wide: true,
    fields: await projectFields(project),
    onOpen: wireUppercase,
    onSubmit: async (data) => {
      await api.patch(`/projects/${project.id}`, data);
      toast(t('Projekt gespeichert'));
      onSaved();
    },
  });
}

const COLUMNS = [tk('Projekt'), tk('Kunde'), tk('Status'), tk('Ampel'), tk('Aufgaben'), tk('Risiko'), tk('Nächstes Gate'), tk('Budget')];

export async function projectsView(container: HTMLElement, _params: Record<string, string>, query: URLSearchParams): Promise<void> {
  const archived = query.get('archived') === 'true';
  const { items } = await api.get(`/projects${archived ? '?archived=true' : ''}`);
  invalidate('projects');
  mount(
    container,
    html`${pageHeader(
      t('Portfolio'),
      archived ? t('Archivierte Projekte') : t('Projekte'),
      t('Status, Ampel und Aufmerksamkeitspunkte aller Projekte.'),
      html`<a class="p-button p-button--ghost" href="/projects${archived ? '' : '?archived=true'}">${archived ? t('Aktive anzeigen') : t('Archiv')}</a>
        ${canWrite() ? html`<button class="p-button p-button--primary" type="button" data-create>${t('Neues Projekt')}</button>` : ''}`,
    )}
    ${
      items.length
        ? html`<div class="p-table-wrap"><table class="p-table pma-table">
            <caption class="p-visually-hidden">${t('Projekte')}</caption>
            <thead><tr>${COLUMNS.map((c) => html`<th scope="col">${t(c)}</th>`)}</tr></thead>
            <tbody>${items.map(
              (p: any) => html`<tr>
                <td data-label="${t('Projekt')}"><a class="pma-project-link" href="/projects/${p.id}">
                  <span class="pma-key" style="${p.color ? `--pma-project-color:${p.color}` : ''}">${p.key}</span> ${p.name}</a>
                  <div class="pma-muted pma-small">${formatDate(p.starts_on)} – ${formatDate(p.ends_on)}</div></td>
                <td data-label="${t('Kunde')}">${p.client_name ?? '–'}</td>
                <td data-label="${t('Status')}">${statusBadge(p.status, PROJECT_STATUS)}</td>
                <td data-label="${t('Ampel')}">${healthBadge(p)}</td>
                <td data-label="${t('Aufgaben')}">${p.open_tasks}${p.overdue_tasks ? html` <span class="pma-overdue">(${t('{n} überfällig', { n: p.overdue_tasks })})</span>` : ''}</td>
                <td data-label="${t('Risiko')}">${p.open_risks ? badge(`${p.open_risks} · ${t('max {n}', { n: p.max_risk_score })}`, riskTone(p.max_risk_score)) : '–'}</td>
                <td data-label="${t('Nächstes Gate')}"><span class="${isOverdue(p.next_gate_due) ? 'pma-overdue' : ''}">${formatDate(p.next_gate_due)}</span></td>
                <td data-label="${t('Budget')}">${formatMoney(p.budget_planned, p.currency)}</td>
              </tr>`,
            )}</tbody></table></div>`
        : emptyState(
            archived ? t('Keine archivierten Projekte') : t('Noch keine Projekte'),
            archived ? t('Archivierte Projekte erscheinen hier.') : t('Lege dein erstes Projekt an – mit Vorlage entstehen Phasen, Gates und Startaufgaben automatisch.'),
            canWrite() && !archived ? html`<button class="p-button p-button--primary" type="button" data-create>${t('Neues Projekt')}</button>` : undefined,
          )
    }`,
  );
  for (const button of container.querySelectorAll('[data-create]')) button.addEventListener('click', () => void openProjectCreate());
}
